export class BoundedTtlCache<Key, Value> {
  private readonly entries = new Map<Key, { value: Value; expiresAt: number }>();

  constructor(
    private readonly capacity: number,
    private readonly ttlMs: number,
  ) {
    if (capacity < 1) {
      throw new Error('BoundedTtlCache capacity must be at least 1');
    }
  }

  get(key: Key, now = Date.now()): Value | undefined {
    const entry = this.entries.get(key);
    if (!entry) {
      return undefined;
    }
    if (entry.expiresAt <= now) {
      this.entries.delete(key);
      return undefined;
    }

    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  set(key: Key, value: Value, now = Date.now()): void {
    this.purgeExpired(now);
    this.entries.delete(key);
    this.entries.set(key, { value, expiresAt: now + this.ttlMs });

    while (this.entries.size > this.capacity) {
      const oldestKey = this.entries.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      this.entries.delete(oldestKey);
    }
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }

  private purgeExpired(now: number): void {
    for (const [key, entry] of this.entries) {
      if (entry.expiresAt <= now) {
        this.entries.delete(key);
      }
    }
  }
}
